import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout, FieldGrid,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Raise Funds for Billing → Fee Raise (One-Time) / Fee Raise (Recurring).
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Fee Raise (One-Time)" for
 * One-Time, and "Raise Funds for Billing (Recurring)" then "Fee Raise
 * (Recurring)" for Recurring. The recurring schedule is entered in the custom
 * component c:prosperityRecurringFees, whose on-screen labels are not in the
 * flow metadata - this page lists what it records (from the flow's outputs)
 * rather than quoting its fields. Both sub-categories pick from the
 * household's sleeve list (Get_Sleeve_Records, unfiltered). Shared chrome and
 * policy copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Raise Funds for Billing',
    subCategory: 'Fee Raise (One-Time) or Fee Raise (Recurring)',
    subject: subjectRow('Fee raise - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which sleeves you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>One-Time and Recurring use different screens</strong> - follow step 2 for a one-time raise, or
        step 3 for a recurring one.
      </>
    ),
  }),
  {
    title: 'Fee Raise (One-Time): pick the sleeves and describe the raise',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          One screen: <em>Please select the account to raise funds for on a One-Time basis</em>, then the{' '}
          <strong>One-Time Fee Raise (Account Selection)</strong> table. You can tick more than one sleeve.
        </p>
        <p style={{ margin: 0 }}>
          Under it, <em>Please enter any other details the Trading Team may need...</em> is required and becomes the
          case description. The screen has no separate amount field, so put the amount to raise - per sleeve, if
          you ticked several - in this box.
        </p>
      </>
    ),
  },
  {
    title: 'Fee Raise (Recurring): pick the sleeve, then set the schedule',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The first screen, <strong>Raise Funds for Billing (Recurring)</strong>, says <em>Please select the account
          to raise funds from on a recurring basis</em>. Pick one row in the <strong>Recurring Fee Raise (Account
          Selection)</strong> table and click Next.
        </p>
        <p style={{ marginBottom: '0.5rem' }}>
          The <strong>Fee Raise (Recurring)</strong> screen then shows the recurring fee panel. It records:
        </p>
        <FieldGrid rows={[
          ['Amount', 'The amount to raise each time.'],
          ['Schedule', 'How often - daily, weekly, monthly or annually, optionally every few weeks, months or years, and on which day (a weekday, a day of the month, or for example the first Monday).'],
          ['Start and end', 'The first date, and an end date if the series should stop.'],
          ['Capital gains', 'Maximum long-term and short-term gains in dollars, and capital gains notes.'],
          ['Tax withholding', 'Whether taxes are withheld.'],
          ['Details', 'Free text for the Trading team. It becomes the case description.'],
        ]} />
        <p style={{ margin: 0 }}>
          The case shows a one-line summary built from these answers - the schedule, the start date, any end date
          and any gains limits. Check it after you submit.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice at the foot of the screen - it governs timing and what the teams
        can and cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const SLEEVE_COLUMNS = 'Columns: Account Number, Account Details, Model, Cash Balance, Balance.';

// Every question, by sub-category and screen, in the order it can appear.
const QUESTIONS = [
  {
    screen: 'Fee Raise (One-Time)',
    q: 'One-Time Fee Raise (Account Selection)',
    when: 'Fee Raise (One-Time) only - always',
    note: `Multi select - tick every sleeve the fee is raised in. ${SLEEVE_COLUMNS}`,
  },
  {
    q: 'Please enter any other details the Trading Team may need...',
    when: 'Fee Raise (One-Time) only - always',
    note: 'Becomes the case description. Include the amount to raise in each sleeve.',
  },
  {
    screen: 'Raise Funds for Billing (Recurring)',
    q: 'Recurring Fee Raise (Account Selection)',
    when: 'Fee Raise (Recurring) only - always',
    note: `Single select - pick the one sleeve the recurring fee is raised from. ${SLEEVE_COLUMNS}`,
  },
  {
    screen: 'Fee Raise (Recurring)',
    q: 'Recurring fee panel',
    when: 'Fee Raise (Recurring) only - always',
    note: 'Amount, schedule, start and end dates, capital gains limits and notes, tax withholding, and details - see step 3 above.',
  },
  {
    screen: 'Both sub-categories',
    q: 'Acknowledgement',
    when: 'Always, under the Operations and Trading notice on the last screen',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Raise Funds for Billing'),
  {
    h: 'There is no amount field on the one-time screen',
    p: ['Fee Raise (One-Time) has no amount question. Put the amount in the details box - it is the case description the Trading team reads.'],
  },
  troubleSleeveRows('These tables are not filtered: every sleeve on the household is listed, highest cash balance first - inactive sleeves and Distribution, Contribution and cancelled (CNX) sleeves included. Check the account number carefully before you tick a row.'),
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function RaiseFundsForBilling() {
  return (
    <GuidePage
      title="Raise Funds for Billing"
      icon="receipt"
      path="Trading → Raise Funds for Billing → Fee Raise (One-Time) or Fee Raise (Recurring)"
      minutes={4}
      intro="How to ask the Prosperity Trading team to raise cash for fees, once or on a schedule - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready.</Para>
        <Bullets items={[
          <><strong>The sleeve or sleeves the fee is raised in</strong>, under the client&rsquo;s household. You pick them from a list.</>,
          <><strong>The amount</strong> - per sleeve for a one-time raise across several sleeves.</>,
          <><strong>For a recurring raise, the schedule</strong>: how often, on which day, the start date and any end date.</>,
          <><strong>Any capital gains limits</strong> (maximum long-term and short-term gains in dollars) and the tax withholding decision, for a recurring raise.</>,
        ]} />
        <Callout>
          A one-time raise can cover several sleeves on one case. A recurring raise case is set up against a
          single sleeve.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare, or to check which screen you should be seeing for the sub-category you chose.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="trading"
        after={(
          <Para>
            A <strong>Fee Raise (Recurring)</strong> case is saved with the schedule and is marked to create recurring
            Trading tasks (Raise Funds for Billing - Fee Raise) from it, on the schedule you set.
          </Para>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It does not describe how fees are calculated or billed, or
        the tax consequences for the client.
      </Footnote>
    </GuidePage>
  );
}
